import { ApolloProvider } from '@apollo/client/react';
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { initAgGridUi } from 'fso-fincrime-ag-grid-ui';
import {
  localStorageUtils,
  GenericContext,
  ActiveRoleContext,
  THEME_EY,
  ErrorPage,
  PermissionTypes,
  IAccessToken,
  AlertsManager,
  client,
  SDK_CONSTANTS,
  ISlaSettingsEntity,
  dateUtils,
  LoginPage,
  IUser,
  SDK_EVENTS_CONSTANTS,
  AuthManager,
  authUtils,
  store,
  IActiveRole,
  SuspensePageLoader,
  BaseLayout,
} from 'fso-fincrime-sdk-ui';
import React, { lazy, useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { Provider } from 'react-redux';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';

import AIDisclaimerContainer from './components/disclaimer/AIDisclaimerContainer';
import Inbox from './pages/Inbox';
import IRoute from './ts/entities/IRoute';
import { CONSTANTS } from './utils/constants/constants';

// Pages are lazy loaded except Inbox as it is the landing page after login
const DueDiligenceTask = lazy(() => import('./components/task/dd/DueDiligenceTask'));
const TaskQuestionnaire = lazy(
  () => import('./components/task/dynamic-questionaire/TaskQuestionnaire')
);
const EntityRelationship = lazy(
  () => import('./components/task/entity-relationship/EntityRelationship')
);
const Narrative = lazy(() => import('./components/task/narrative/Narrative'));
const Rfi = lazy(() => import('./components/task/rfi/Rfi'));
const SarUi = lazy(() => import('./components/task/sar/SarUi'));
const SarFillingUi = lazy(() => import('./components/task/sar-filling/SarFillingUi'));
const TaskCase360 = lazy(() => import('./components/task/task-case-360/TaskCase360'));
const TaskTimer = lazy(() => import('./components/task/task-timer/TaskTimer'));
const TransactionReviewer = lazy(() => import('./components/task/tr/TransactionReviewer'));
const ScreeningTab = lazy(() => import('./components/task/screening/ScreeningTab'));
const CaseDetail = lazy(() => import('./pages/case-detail/CaseDetail'));
const ConfigPage = lazy(() => import('./pages/config-page/ConfigPage'));
const Task = lazy(() => import('./pages/task/Task'));
const CaseList = lazy(() => import('./pages/work-items/CaseList'));
const DataPointProviderIntegrationTab = lazy(
  () => import('./components/task/data-point-provider-integration/DataPointProviderIntegrationTab')
);
const AboutPage = lazy(() =>
  import('fso-fincrime-about-page-ui').then((m) => ({ default: m.AboutPage }))
);
import 'material-icons';
import './utils/i18n/i18n';
import 'fso-fincrime-sdk-ui/dist/index.css';
import 'fso-fincrime-ag-grid-ui/dist/index.css';
import 'fso-fincrime-about-page-ui/dist/index.css';
import 'fso-fincrime-document-center-ui/dist/index.css';
import './App.scss';

/**
 * Main App component
 */
function App(): React.JSX.Element {
  const { i18n } = useTranslation();
  const homePageIcons = localStorageUtils.getSavedItem(
    SDK_CONSTANTS.LOCAL_STORAGE_KEYS.PROJECT_SETTINGS.HOME_PAGE_ICONS
  );
  const [, setCookie] = useCookies([SDK_CONSTANTS.COOKIES.ASSETS]);
  const userInfo: IAccessToken = localStorageUtils.getSavedItem(
    SDK_CONSTANTS.LOCAL_STORAGE_KEYS.USER_INFO
  );
  const projectSettingsName = localStorageUtils.getSavedItem(
    SDK_CONSTANTS.LOCAL_STORAGE_KEYS.PROJECT_SETTINGS.PROJECT_NAME
  );
  const projectSettingsTenant = localStorageUtils.getSavedItem(
    SDK_CONSTANTS.LOCAL_STORAGE_KEYS.PROJECT_SETTINGS.TENANT
  );

  const [showAuth, setShowAuth] = useState(false);
  const [caseHeaderToggle, setCaseHeaderToggle] = useState(true);
  const [fileType, setFileType] = useState<string[]>([]);
  const [maxFileSize, setMaxFileSize] = useState<number | undefined>(undefined);
  const [user, setUser] = useState<IUser | undefined>(undefined);
  const [slaSettingsEntity, setSlaSettingsEntity] = useState<ISlaSettingsEntity | undefined>(
    undefined
  );
  const [dateFormat, setDateFormat] = useState<string>(dateUtils.getUserDateFormat());
  const [routing, setRouting] = useState<boolean>(
    localStorageUtils.getSavedItem(SDK_CONSTANTS.LOCAL_STORAGE_KEYS.ROUTING) ?? false
  );
  const [activeRole, setActiveRole] = useState<IActiveRole[]>(
    localStorageUtils.getSavedItem(SDK_CONSTANTS.LOCAL_STORAGE_KEYS.USER.ACTIVE_ROLE)
  );
  const [selectedProject, setSelectedProject] = useState<string>(authUtils.getDefaultProject());
  const [selectedLanguage, setSelectedLanguage] = useState<string>(i18n.language);

  useEffect(() => {
    localStorageUtils.removeSavedItem(SDK_CONSTANTS.LOCAL_STORAGE_KEYS.CASE_PERMISSIONS);
  }, []);

  useEffect((): void => {
    if (userInfo) {
      authUtils
        .handleProjectSwitch(
          selectedProject,
          setFileType,
          setMaxFileSize,
          setSlaSettingsEntity,
          setRouting
        )
        .then((securtyAssets) => {
          setCookie(
            SDK_CONSTANTS.COOKIES.ASSETS,
            {
              assets: securtyAssets,
            },
            { path: '/', sameSite: 'none', secure: true }
          );
        })
        .catch(() => {
          // TODO: If this fails the project should go to error page and stay there
        });
    }
  }, [selectedProject]);

  // Register SDK event listeners once and cleanup on unmount
  useEffect(() => {
    const handleDateFormatChange = (e: Event): void => {
      const customEvent = e as CustomEvent<string>;
      setDateFormat(customEvent.detail);
    };
    const handleRoutingChange = (e: Event): void => {
      const customEvent = e as CustomEvent<boolean>;
      setRouting(customEvent.detail);
    };
    const handleHeaderExpandStatus = (e: Event): void => {
      const customEvent = e as CustomEvent<boolean>;
      setCaseHeaderToggle(customEvent.detail);
    };
    const handleActiveRoleUpdated = (e: Event): void => {
      const customEvent = e as CustomEvent<IActiveRole[]>;
      setActiveRole(customEvent.detail);
    };
    const handleSelectedLanguageUpdate = (e: Event): void => {
      const customEvent = e as CustomEvent<string>;
      setSelectedLanguage(customEvent.detail);
    };

    document.addEventListener(
      SDK_EVENTS_CONSTANTS.DATE_FORMAT_CHANGE,
      handleDateFormatChange as EventListener
    );
    document.addEventListener(
      SDK_EVENTS_CONSTANTS.ROUTING_CHANGE,
      handleRoutingChange as EventListener
    );
    document.addEventListener(
      SDK_EVENTS_CONSTANTS.HEADER_EXPAND_STATUS,
      handleHeaderExpandStatus as EventListener
    );
    document.addEventListener(
      SDK_EVENTS_CONSTANTS.ACTIVE_ROLE_UPDATED,
      handleActiveRoleUpdated as EventListener
    );
    document.addEventListener(
      SDK_EVENTS_CONSTANTS.SELECTED_LANGUAGE_UPDATE,
      handleSelectedLanguageUpdate as EventListener
    );

    return (): void => {
      document.removeEventListener(
        SDK_EVENTS_CONSTANTS.DATE_FORMAT_CHANGE,
        handleDateFormatChange as EventListener
      );
      document.removeEventListener(
        SDK_EVENTS_CONSTANTS.ROUTING_CHANGE,
        handleRoutingChange as EventListener
      );
      document.removeEventListener(
        SDK_EVENTS_CONSTANTS.HEADER_EXPAND_STATUS,
        handleHeaderExpandStatus as EventListener
      );
      document.removeEventListener(
        SDK_EVENTS_CONSTANTS.ACTIVE_ROLE_UPDATED,
        handleActiveRoleUpdated as EventListener
      );
      document.removeEventListener(
        SDK_EVENTS_CONSTANTS.SELECTED_LANGUAGE_UPDATE,
        handleSelectedLanguageUpdate as EventListener
      );
    };
  }, []);

  const genericContextValue = useMemo(
    () => ({
      dateFormat,
      caseHeaderToggle,
      routing,
      setSelectedProject,
      selectedProject,
      selectedLanguage,
    }),
    [dateFormat, caseHeaderToggle, routing, selectedProject, selectedLanguage]
  );

  const tabsRoutes = (): React.JSX.Element => {
    return (
      <>
        <Route
          path="narrative/:tabId"
          element={
            <SuspensePageLoader>
              <Narrative />
            </SuspensePageLoader>
          }
        />
        <Route
          path="transactionReviewer/:tabId"
          element={
            <SuspensePageLoader>
              <TransactionReviewer />
            </SuspensePageLoader>
          }
        />
        <Route
          path="entityRelationship/:tabId"
          element={
            <SuspensePageLoader>
              <EntityRelationship />
            </SuspensePageLoader>
          }
        />
        <Route
          path="sar-ui/:tabId/*"
          element={
            <SuspensePageLoader>
              <SarUi />
            </SuspensePageLoader>
          }
        />
        <Route
          path="sar-filling/:tabId/*"
          element={
            <SuspensePageLoader>
              <SarFillingUi />
            </SuspensePageLoader>
          }
        />
        <Route
          path="case360/:tabId"
          element={
            <SuspensePageLoader>
              <TaskCase360 />
            </SuspensePageLoader>
          }
        />
        <Route
          path="rfi"
          element={
            <SuspensePageLoader>
              <Rfi />
            </SuspensePageLoader>
          }
        />
        <Route
          path="due-diligence/:tabId"
          element={
            <SuspensePageLoader>
              <DueDiligenceTask maxFileSize={maxFileSize} />
            </SuspensePageLoader>
          }
        />
        <Route
          path="questionnaire/:tabId"
          element={
            <SuspensePageLoader>
              <TaskQuestionnaire />
            </SuspensePageLoader>
          }
        />
        <Route
          path="timer/:tabId"
          element={
            <SuspensePageLoader>
              <TaskTimer />
            </SuspensePageLoader>
          }
        />
        <Route
          path="screening/:tabId"
          element={
            <SuspensePageLoader>
              <ScreeningTab />
            </SuspensePageLoader>
          }
        />
        <Route
          path="data-point-provider-integration/:tabId"
          element={
            <SuspensePageLoader>
              <DataPointProviderIntegrationTab />
            </SuspensePageLoader>
          }
        />
      </>
    );
  };

  const routes: IRoute[] = [
    {
      path: `${SDK_CONSTANTS.PAGES_URL.TASK}/:id`,
      element: (
        <ApolloProvider client={client}>
          <Task
            fileType={fileType}
            maxFileSize={maxFileSize}
            slaLabels={slaSettingsEntity?.slaLabels}
          />
        </ApolloProvider>
      ),
      child: tabsRoutes(),
      permissions: [],
    },
    {
      path: `${SDK_CONSTANTS.PAGES_URL.CASE_DETAILS}/:caseId`,
      element: (
        <ApolloProvider client={client}>
          <CaseDetail fileType={fileType} maxFileSize={maxFileSize} />
        </ApolloProvider>
      ),
      child: (
        <Route
          path="task/:id"
          element={
            <Task
              fileType={fileType}
              maxFileSize={maxFileSize}
              slaLabels={slaSettingsEntity?.slaLabels}
            />
          }>
          {tabsRoutes()}
        </Route>
      ),
      permissions: [],
    },
    {
      path: SDK_CONSTANTS.PAGES_URL.INBOX,
      element: <Inbox withChart />,
      permissions: [],
    },
    {
      path: SDK_CONSTANTS.PAGES_URL.TASK_LIST,
      element: <Inbox />,
      permissions: [],
    },
    {
      path: SDK_CONSTANTS.PAGES_URL.CASE_LIST,
      element: (
        <CaseList
          showCaseCreationModule
          showViewsModule
          showResetModule
          showBulkSelectionModule
          showWorkflowSelector
        />
      ),
      permissions: [PermissionTypes.CASE_LIST],
    },
    {
      path: SDK_CONSTANTS.PAGES_URL.ABOUT_PAGE,
      element: <AboutPage />,
      permissions: [],
    },
    {
      path: SDK_CONSTANTS.PAGES_URL.CONFIG_PAGE,
      element: <ConfigPage />,
      permissions: [],
    },
    {
      path: '/',
      element: <Navigate to={SDK_CONSTANTS.PAGES_URL.INBOX} />,
      permissions: [],
    },
    {
      path: SDK_CONSTANTS.PAGES_URL.LOGIN,
      element: <Navigate to={SDK_CONSTANTS.PAGES_URL.INBOX} />,
      permissions: [],
    },
    {
      path: '*',
      element: <ErrorPage toRedirect pageType="404" />,
      permissions: [],
    },
  ];

  const getLoginRoutes = (): React.JSX.Element => {
    return (
      <Routes>
        <Route
          path={SDK_CONSTANTS.PAGES_URL.LOGIN.replace('/', '')}
          element={
            <SuspensePageLoader>
              <LoginPage setShowAuth={setShowAuth} />
            </SuspensePageLoader>
          }
        />
        <Route path="*" element={<Navigate to={SDK_CONSTANTS.PAGES_URL.LOGIN} />} />
      </Routes>
    );
  };

  const getAuthRecord = (): React.JSX.Element => {
    // we are authenticated we can now init ag grid
    initAgGridUi();
    return (
      <AuthManager
        setShowAuth={setShowAuth}
        setFileType={setFileType}
        setMaxFileSize={setMaxFileSize}
        setUser={setUser}
        setSlaSettingsEntity={setSlaSettingsEntity}
        setSelectedProject={setSelectedProject}
        setRouting={setRouting}>
        <>
          {userInfo && projectSettingsName && projectSettingsTenant && user?.isActive && (
            <BaseLayout
              projects={user.projects}
              selectedProject={selectedProject}
              setSelectedProject={setSelectedProject}
              isToShowNotificationCenter
              isToShowSettingsMenu
              topItems={[...CONSTANTS.VERTICAL_BAR_ITEMS.TOP, ...homePageIcons]}
              bottomItems={CONSTANTS.VERTICAL_BAR_ITEMS.BOTTOM}>
              <ActiveRoleContext.Provider value={activeRole}>
                <GenericContext.Provider value={genericContextValue}>
                  <Routes>
                    {routes.map((route) => (
                      <Route
                        key={route.path}
                        path={route.path}
                        element={<SuspensePageLoader>{route.element}</SuspensePageLoader>}>
                        {route.child}
                      </Route>
                    ))}
                  </Routes>

                  {/* AI Disclaimer component that handles showing/hiding based on cookie */}
                  {window.__RUNTIME_CONFIG__?.REACT_APP_DISCLAIMER_TEXT && (
                    <AIDisclaimerContainer
                      disclaimerCookieName={SDK_CONSTANTS.COOKIES.DISCLAIMER_ACK}
                      requireProjectSettings
                      disclaimerConfigKey="REACT_APP_DISCLAIMER_TEXT"
                    />
                  )}
                </GenericContext.Provider>
              </ActiveRoleContext.Provider>
            </BaseLayout>
          )}
        </>
      </AuthManager>
    );
  };

  return (
    <Provider store={store}>
      <ThemeProvider theme={THEME_EY}>
        <BrowserRouter basename="/">
          {!userInfo && !showAuth ? getLoginRoutes() : getAuthRecord()}
        </BrowserRouter>
        <AlertsManager />
        <CssBaseline />
      </ThemeProvider>
    </Provider>
  );
}

export default App;
